// @ts-nocheck
import { Cell, DynamicTable, RddkTheme } from 'db-npm-e4sc-rd-ui';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { MAX_LOANS_MOBILE } from '../../config';
import { getFieldKeysToDisplay } from '../../helpers/table';
import { useLoansState } from '../../state/LoanCalculatorContext';
import { FieldKeys } from '../../types/FieldKey';
import { CalculatorAddLoanButtonMobile } from './CalculatorAddLoanButtonMobile';
import { CalculatorTableBodyCell } from './CalculatorTableBodyCell';
import { CalculatorTableBodyLabelCell } from './CalculatorTableBodyLabelCell';
import { CalculatorTableErrorMessage } from './CalculatorTableErrorMessage';
import { CalculatorTableHeadCell } from './CalculatorTableHeadCell';

type CalculatorTableMobileProps = {
  fieldKeys: FieldKeys;
  showHeader: boolean;
};

export const CalculatorTableMobile: React.FC<CalculatorTableMobileProps> = ({ fieldKeys, showHeader }) => {
  const { t } = useTranslation();
  const { loans, lastCalculationDate } = useLoansState();

  const canAddLoan = loans.length < MAX_LOANS_MOBILE;

  const fieldsKeysToDisplay = getFieldKeysToDisplay(fieldKeys, loans);

  /**
   * Configure the table data for mobile viewport
   */
  const mobileTableData = React.useMemo(() => {
    const rows: Cell[][] = [];

    // Add table header row, if "showHeader" is true
    if (showHeader) {
      const headerRow: Cell[] = [];

      loans.slice(0, MAX_LOANS_MOBILE).forEach((loan, index) => {
        headerRow.push({
          value: loan.type,
          suffix: !!loan.type && t(`calculator-loan-${loan.type}-text`),
          cellValueRender: () => <CalculatorTableHeadCell loanArrayIndex={index} loan={loan} />,
        });
      });

      // Add the "Add loan" button as a table header cell
      if (canAddLoan) {
        headerRow.push({
          cellRender: () => <CalculatorAddLoanButtonMobile />,
          style: {
            position: 'relative',
            padding: 0,
            margin: 0,
          },
        });
      }

      rows.push(headerRow);
    }

    // Iterate through all keys and add them as rows
    fieldsKeysToDisplay.forEach((fieldKey, fieldIndex) => {
      const bodyRow: Cell[] = [];

      loans.slice(0, MAX_LOANS_MOBILE).forEach((loan, loanIndex) => {
        if (loan.status === 'error') {
          // If the loan status is "error", then display the error mesage, but only for the first row
          if (fieldIndex === 0) {
            bodyRow.push({
              value: <CalculatorTableErrorMessage loanArrayIndex={loanIndex} />,
              colSpan: canAddLoan ? 2 : 1,
              width: canAddLoan ? '100%' : '50%',
              rowSpan: 99,
              style: {
                verticalAlign: 'middle',
                background: RddkTheme.colors.grey10,
              },
            });
          }
        } else {
          // If no error, then show the tableItem content
          const tableItemForLoan = loan.tableItems?.find((i) => i.fieldKey === fieldKey);
          bodyRow.push({
            prefix: <CalculatorTableBodyLabelCell fieldKey={fieldKey} />,
            value: <CalculatorTableBodyCell loanArrayIndex={loanIndex} tableItem={tableItemForLoan} />,
            colSpan: canAddLoan ? 2 : 1,
            width: canAddLoan ? '100%' : '50%',
          });
        }
      });

      rows.push(bodyRow);
    });

    return rows;
  }, [t, loans, showHeader, fieldsKeysToDisplay, canAddLoan]);

  return (
    <TableContainer>
      <DynamicTable key={lastCalculationDate} striped={true} firstRowIsHead={showHeader} data={mobileTableData} />
    </TableContainer>
  );
};

const TableContainer = styled.div`
  table {
    table-layout: fixed; /* IMPORTANT */
  }
`;
